import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Grid,
  MenuItem,
  IconButton,
  Divider,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import { patchesAPI } from '../services/api';
import { usePermissions } from '../context/PermissionsContext';

const CLASSIFICATIONS = [
  'Critical',
  'Important',
  'Moderate',
  'Low',
  'Unspecified',
];

const emptyPatch = {
  kb_number: '',
  description: '',
  classification: 'Unspecified',
  date_applied: new Date().toISOString().slice(0, 7), // YYYY-MM format
};

function PatchForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { isAdmin, loading: permissionsLoading } = usePermissions();
  const readOnly = !permissionsLoading && !isAdmin();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    kb_number: '',
    description: '',
    classification: 'Unspecified',
    date_applied: new Date().toISOString().slice(0, 7), // YYYY-MM format
  });
  const [patches, setPatches] = useState([{ ...emptyPatch }]);

  useEffect(() => {
    if (readOnly && !isEdit) navigate('/patches', { replace: true });
  }, [readOnly, isEdit, navigate]);

  useEffect(() => {
    if (isEdit) {
      loadPatch();
    }
  }, [id]);

  const loadPatch = async () => {
    try {
      const response = await patchesAPI.get(id);
      setFormData({
        kb_number: response.data.kb_number,
        description: response.data.description,
        classification: response.data.classification,
        date_applied: response.data.date_applied?.slice(0, 7) || '',
      });
    } catch (err) {
      setError('Failed to load patch');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePatchChange = (index, field, value) => {
    const newPatches = [...patches];
    newPatches[index][field] = value;
    setPatches(newPatches);
  };

  const handleAddPatch = () => {
    setPatches([...patches, { ...emptyPatch }]);
  };

  const handleRemovePatch = (index) => {
    if (patches.length > 1) {
      const newPatches = patches.filter((_, i) => i !== index);
      setPatches(newPatches);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (isEdit) {
        // Single patch update
        const submitData = {
          ...formData,
          date_applied: formData.date_applied + '-01'
        };
        await patchesAPI.update(id, submitData);
      } else {
        // Multiple patches creation
        const createPromises = patches.map(patch => {
          const submitData = {
            ...patch,
            date_applied: patch.date_applied + '-01'
          };
          return patchesAPI.create(submitData);
        });
        await Promise.all(createPromises);
      }
      navigate('/patches');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save patch(es)');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {readOnly ? 'View Patch' : isEdit ? 'Edit Patch' : 'Add New Patches'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mt: 3 }}>
        <form onSubmit={handleSubmit}>
          <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
          {isEdit ? (
            // Single patch edit mode
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  required
                  fullWidth
                  label="KB Number"
                  name="kb_number"
                  value={formData.kb_number}
                  onChange={handleChange}
                  placeholder="e.g., KB5001234"
                  disabled={isEdit}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  required
                  fullWidth
                  label="Month Applied"
                  name="date_applied"
                  type="month"
                  value={formData.date_applied}
                  onChange={handleChange}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  required
                  fullWidth
                  select
                  label="Classification"
                  name="classification"
                  value={formData.classification}
                  onChange={handleChange}
                >
                  {CLASSIFICATIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  required
                  fullWidth
                  multiline
                  rows={4}
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Description of what this patch does"
                />
              </Grid>
            </Grid>
          ) : (
            // Multiple patches add mode
            <Box>
              {patches.map((patch, index) => (
                <Box key={index} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Patch {index + 1}</Typography>
                    {patches.length > 1 && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemovePatch(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        required
                        fullWidth
                        label="KB Number"
                        value={patch.kb_number}
                        onChange={(e) => handlePatchChange(index, 'kb_number', e.target.value)}
                        placeholder="e.g., KB5001234"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        required
                        fullWidth
                        label="Month Applied"
                        type="month"
                        value={patch.date_applied}
                        onChange={(e) => handlePatchChange(index, 'date_applied', e.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        required
                        fullWidth
                        select
                        label="Classification"
                        value={patch.classification}
                        onChange={(e) => handlePatchChange(index, 'classification', e.target.value)}
                      >
                        {CLASSIFICATIONS.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        required
                        fullWidth
                        multiline
                        rows={4}
                        label="Description"
                        value={patch.description}
                        onChange={(e) => handlePatchChange(index, 'description', e.target.value)}
                        placeholder="Description of what this patch does"
                      />
                    </Grid>
                  </Grid>
                  {index < patches.length - 1 && <Divider sx={{ mt: 3 }} />}
                </Box>
              ))}

              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddPatch}
                sx={{ mb: 3 }}
              >
                Add Another Patch
              </Button>
            </Box>
          )}

          </fieldset>
          <Box sx={{ mt: 3, display: 'flex', gap: 2, pt: 2, borderTop: '1px solid #ddd' }}>
            {!readOnly && (
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={saving}
            >
              {saving ? 'Saving...' : isEdit ? 'Update' : `Save ${patches.length} Patch${patches.length > 1 ? 'es' : ''}`}
            </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={() => navigate('/patches')}
              disabled={saving}
            >
              {readOnly ? 'Back' : 'Cancel'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}

export default PatchForm;
